import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function QualityApprovalModal({ screen, currentUser, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const approvalCount = screen.qualityApprovals?.length || 0;
  const approvalsNeeded = 2 - approvalCount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!reason.trim()) {
      setError('Please provide a reason for approval');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/silkscreens/${screen.id}/quality-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser?.id || 'system',
          userName: currentUser?.name || 'System User',
          reason: reason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add quality approval');
      }

      onSuccess();
      onClose(); // Close modal after successful approval
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#F44336',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          ✓ Quality Approval
        </h2>

        <div style={{
          background: '#ffebee',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '0.9rem',
          color: '#c62828'
        }}>
          <strong>{screen.screenCode}</strong> - {screen.currentCustomerName}
          <br />
          {screen.currentItemDescription}
          <br />
          <div style={{ marginTop: '8px', fontWeight: '600' }}>
            {approvalCount > 0 ? (
              <>
                ✓ {approvalCount} approval{approvalCount > 1 ? 's' : ''} received
                <br />
                {approvalsNeeded > 0 && `${approvalsNeeded} more approval${approvalsNeeded > 1 ? 's' : ''} needed to clear hold`}
              </>
            ) : (
              '2 approvals required to clear quality hold'
            )}
          </div>
        </div>

        {screen.qualityApprovals && screen.qualityApprovals.length > 0 && (
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.85rem'
          }}>
            <strong>Previous Approvals:</strong>
            {screen.qualityApprovals.map((approval, idx) => (
              <div key={idx} style={{ marginTop: '8px', paddingLeft: '8px', borderLeft: '3px solid #4CAF50' }}>
                <div style={{ fontWeight: '600' }}>{approval.userName}</div>
                <div style={{ color: '#666' }}>{approval.reason}</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '4px' }}>
                  {new Date(approval.approvedAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              Approval Reason *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Minor damage acceptable for this production run, within tolerance limits, etc."
              rows="4"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#F44336'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
              Explain why this screen is acceptable despite quality issues
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#e0e0e0')}
              onMouseLeave={(e) => e.target.style.background = '#f5f5f5'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: loading ? '#b0bec5' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#43A047')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#4CAF50')}
            >
              {loading ? 'Submitting...' : 'Approve Quality'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default QualityApprovalModal;
